import console from 'console'
import * as React from 'react'
import memoize from '../../../../common/src/memoize'
import {
  LeasingTask,
  FetchInboxQuery,
  FetchTasksPageQuery,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import validate, { ValidatorResp, Validator } from '../../../../common/src/validate'
import EditBox, { Field } from './EditBox'
import EditBoxLarge from './EditBoxLarge'

type LeasingSpecialsWithIndex = NonNullable<LeasingTask['leasingInfo']>['leasingSpecials'][0] & { index: string }
const textField: Field<string | null | undefined> = {
  key: 'text',
  isEditable: true,
  label: 'Leasing Specials',
  placeholder: '$100 off your first month of rent...',
  editTextFunc: v => v || '',
  textFunc: v => v || 'leasing special?',
  validator: validate.str().empty('').preventEmojis(),
}
const endDateField: Field<string | null | undefined> = {
  key: 'endDate',
  isEditable: true,
  editTextFunc: v => v || '',
  textFunc: v => v || 'end date?',
  placeholder: 'date',
  validator: validate.isoDate().empty(''),
}
const fields: Field<string | null | undefined>[] = [textField, endDateField]
const DefaultExpirationText = 'No Expiry'

/**
 * Takes in a leasing task that is a of type leasing property, in order to display leasing specials. User is used for only showing this component to super admins.
 * The onEdit func is used so that if a field is edited, it has a function to callback to to update a model - usually done via a GraphQL request.
 *
 * The state has an editKey which represents which field you are editing, which is in this case, either the text or end date of a leasing special.
 * The error is a message string that is used to display error messages.
 */
export default class LeasingSpecialsSection extends React.PureComponent<
  {
    leasingTask: NonNullable<FetchInboxQuery['thread']>['leasingTask'] | NonNullable<FetchTasksPageQuery['task']>
    user: FetchUserContextQuery['self']
    onEdit: (key: string, value: any) => any
  },
  {
    editKey: string | null
    error?: string
  }
> {
  private readonly focusHandlers = {}
  private readonly blurHandlers = {}
  private readonly editHandlers = {}
  private readonly keyDownHandlers = {}
  private readonly onBlurHandlers = {}
  constructor(props: LeasingSpecialsSection['props']) {
    super(props)
    this.state = {
      editKey: null,
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onWindowKeyDownBound)
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onWindowKeyDownBound)
  }

  render() {
    const { leasingTask, user } = this.props
    const { editKey, error } = this.state
    const canEdit = user.permissions.canModifyLeasingSpecials
    const leasingSpecialsWithIndex = leasingTask.leasingInfo?.leasingSpecials?.map((item, i) => ({
      ...item,
      index: i + '',
    })) as LeasingSpecialsWithIndex[]
    const leasingSpecialsPairs = this.makeLeasingSpecials(
      leasingSpecialsWithIndex[0] ||
        ({
          text: null as unknown,
          endDate: null,
          __typename: 'LeasingSpecial',
          index: '0',
        } as LeasingSpecialsWithIndex),
      canEdit,
      editKey,
      error
    )
    return <div>{leasingSpecialsPairs}</div>
  }

  private makeLeasingSpecials(
    leasingSpecial: LeasingSpecialsWithIndex,
    canEdit: boolean,
    editKey: string | null,
    error: string | undefined
  ) {
    const editTextKey = this.makeEditKeyPair(leasingSpecial, 'text')
    const editEndDateKey = this.makeEditKeyPair(leasingSpecial, 'endDate')

    return (
      <div key={`${editTextKey}:${editEndDateKey}`}>
        <EditBoxLarge
          key={editTextKey}
          canEdit={canEdit}
          canDelete={leasingSpecial.text != null}
          onKeyDown={memoize(
            this.keyDownHandlers,
            editTextKey,
            // eslint-disable-next-line @typescript-eslint/unbound-method
            () => this.onKeyDownBound
          )}
          placeholder={textField.placeholder}
          onFocus={memoize(this.focusHandlers, editTextKey, (a: string) => () => this.onFocus(a))}
          onBlur={memoize(
            this.blurHandlers,
            editTextKey,
            // eslint-disable-next-line @typescript-eslint/unbound-method
            () => this.onBlurBound
          )}
          label={textField.label}
          isEditing={editKey === editTextKey}
          error={editKey === editTextKey ? error : undefined}
          body={leasingSpecial.text || ''}
          cascadeState={leasingSpecial.text != null ? 'OWN_VALUE' : 'MISSING'}
          sublabel={leasingSpecial.text != null ? undefined : 'missing'}
          labelColor={leasingSpecial.text != null ? undefined : 'midway'}
          submitEdit={memoize(
            this.editHandlers,
            editTextKey,
            (key: string) => (value: string) => this.submitEdit(value, key, textField.validator)
          )}
        />
        {leasingSpecial.text != null && (
          <EditBox
            key={editEndDateKey}
            val={leasingSpecial.endDate}
            field={endDateField}
            isEditing={editKey === editEndDateKey}
            hasError={editKey === editEndDateKey ? Boolean(error) : false}
            onFocus={memoize(this.focusHandlers, editEndDateKey, (a: string) => () => this.onFocus(a))}
            onKeyDown={memoize(
              this.keyDownHandlers,
              editEndDateKey,
              // eslint-disable-next-line @typescript-eslint/unbound-method
              () => this.onKeyDownBound
            )}
            onBlur={memoize(
              this.onBlurHandlers,
              editEndDateKey,
              // eslint-disable-next-line @typescript-eslint/unbound-method
              () => this.onBlurBound
            )}
            cascadeState={leasingSpecial.endDate != null ? undefined : 'MISSING'}
            disableEdit={!canEdit}
          />
        )}
      </div>
    )
  }

  /**
   * Makes an identifier based on the index and key of a leasing special.
   */
  private makeEditKeyPair(s: LeasingSpecialsWithIndex, key: string) {
    return s.index + ':' + key
  }

  private onFocus(key: string) {
    const { editKey } = this.state
    if (editKey === key) {
      return
    }
    const [, fieldKey] = this.geIndexKeyPair(key)
    const field = fields.find(f => f.key === fieldKey)

    if (field == null) {
      return
    }
    if (!field.isEditable) {
      return
    }
    this.setState({ editKey: key, error: undefined })
  }

  private onBlurBound = () => {
    this.stopEditing()
  }

  private onKeyDownBound = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { editKey } = this.state
    if (editKey == null) {
      return
    }
    const [, fieldKey] = this.geIndexKeyPair(editKey)
    const field = fields.find(f => f.key === fieldKey)
    if (field == null) {
      return
    }
    const { isEditable, options, isOptional } = field
    if (!isEditable) {
      return
    }

    let validator
    if (options != null) {
      validator = validate
        .str()
        .valid(options as string[])
        .caseInsensitive()
      if (isOptional) {
        validator = validator.empty('')
      }
    } else {
      validator = field.validator
    }

    if (e.keyCode === 13 || e.keyCode === 9) {
      // tab or enter
      const str = e.currentTarget.value
      await this.submitEdit(str, editKey, validator)
    } else if (e.key === 'Escape') {
      this.stopEditing()
    } else {
      e.stopPropagation()
    }
  }

  private onWindowKeyDownBound = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.state.editKey != null) {
      this.stopEditing()
    }
  }

  private async submitEdit(
    value: string,
    editKey: string,
    validator: Validator<string | null | undefined> | null | undefined
  ) {
    const val = validator == null ? { value } : validator.validate(value)
    const [index, key] = this.geIndexKeyPair(editKey)
    const numIndex = parseInt(index, 10)
    if (val.error == null) {
      const leasingSpecials = this.props.leasingTask.leasingInfo?.leasingSpecials || []
      const leasingSpecial = leasingSpecials.find((_s, i) => i === numIndex)
      if (leasingSpecial == null && leasingSpecials.length > 0) {
        this.setState({ error: 'Cannot identify leasing special' })
      } else {
        if (this.props.onEdit == null) {
          console.warn('isEditable is set, onEdit is missing: ' + editKey)
        } else {
          // if val.value is undefined and it has the text key then it should be deleted
          await this.addOrDelete(val, key, numIndex, leasingSpecial, leasingSpecials, editKey)
        }
      }
    } else {
      this.setState({ error: val.error.message })
    }
  }

  private async addOrDelete(
    val: ValidatorResp<string | null | undefined>,
    key: string,
    numIndex: number,
    leasingSpecial: NonNullable<LeasingTask['leasingInfo']>['leasingSpecials'][0] | undefined,
    leasingSpecials: NonNullable<LeasingTask['leasingInfo']>['leasingSpecials'],
    editKey: string
  ) {
    if (val.value === undefined && key === 'text') {
      await this.delete(numIndex)
    } else {
      // the first one
      await this.addLeasingSpecial(leasingSpecial, val, key, leasingSpecials, numIndex, editKey)
    }
  }

  private async addLeasingSpecial(
    leasingSpecial: NonNullable<LeasingTask['leasingInfo']>['leasingSpecials'][0] | undefined,
    val: ValidatorResp<string | null | undefined>,
    key: string,
    leasingSpecials: NonNullable<LeasingTask['leasingInfo']>['leasingSpecials'],
    numIndex: number,
    editKey: string
  ) {
    if (leasingSpecial == null) {
      if (val.value != null) {
        await this.addFirstSpecial(val.value)
      }
    } else {
      await this.addToExistingLeasingSpecial(val, leasingSpecial, key, leasingSpecials, numIndex, editKey)
    }
  }

  private async addToExistingLeasingSpecial(
    val: ValidatorResp<string | null | undefined>,
    leasingSpecial: NonNullable<LeasingTask['leasingInfo']>['leasingSpecials'][0],
    key: string,
    leasingSpecials: NonNullable<LeasingTask['leasingInfo']>['leasingSpecials'],
    numIndex: number,
    editKey: string
  ) {
    const cleanVal = val.value == null ? null : val.value
    const updatedLeasingSpecial = {
      ...leasingSpecial,
      [key]: cleanVal,
    }
    const newLeasingSpecials = this.updateObjectInArray(leasingSpecials, {
      index: numIndex,
      item: updatedLeasingSpecial,
    })
    try {
      await this.props.onEdit(
        'leasingSpecials',
        newLeasingSpecials.map(ls => Object.assign({}, ls, { __typename: undefined }))
      )
      this.resetState()
    } catch (e) {
      if (e instanceof Error) {
        this.setState({ error: e.message, editKey })
      } else {
        console.error(e)
      }
    }
  }

  private resetState() {
    this.setState({ error: undefined, editKey: null })
  }

  /**
   * Gets the index key pair that is necessary to identify the leasing special to modify
   */
  private geIndexKeyPair(editKey: string): [string, string] {
    return editKey.split(':') as [string, string]
  }

  /**
   * Deletes a leasing special
   */
  private async delete(index: number) {
    // leasingSpecials is not null in this case
    const leasingSpecials = this.props.leasingTask.leasingInfo?.leasingSpecials || []
    const leasingSpecial = leasingSpecials.find((_s, i) => i === index)
    if (leasingSpecial == null) {
      this.setState({ error: 'Cannot identify leasing special' })
    } else {
      if (
        !confirm(
          `Are you sure you want to delete this leasing special of "${leasingSpecial.text}" with end date of "${
            leasingSpecial.endDate || DefaultExpirationText
          }"`
        )
      ) {
        return
      }
      try {
        await this.props.onEdit(
          'leasingSpecials',
          leasingSpecials.filter((_item, idx) => idx !== index)
        )
        this.resetState()
      } catch (e) {
        if (e instanceof Error) {
          this.setState({ error: e.message, editKey: index.toString() })
        } else {
          console.error(e)
        }
      }
    }
  }

  /**
   * Adds a new leasing special if there are none to begin with.
   */
  private async addFirstSpecial(text: string) {
    const leasingSpecials = this.props.leasingTask.leasingInfo?.leasingSpecials || []
    const newSpecials = leasingSpecials.concat({
      endDate: null,
      text,
      __typename: 'LeasingSpecial',
    })
    try {
      await this.props.onEdit(
        'leasingSpecials',
        newSpecials.map(ls => Object.assign({}, ls, { __typename: undefined }))
      )
      // set the editKey as the newly created leasing special
      this.setState({ error: undefined, editKey: newSpecials.length - 1 + ':' + 'endDate' })
    } catch (e) {
      if (e instanceof Error) {
        this.setState({ error: e.message, editKey: null })
      } else {
        console.error(e)
      }
    }
  }

  private stopEditing() {
    this.setState({ error: undefined, editKey: null })
  }

  private updateObjectInArray<T>(array: T[], action: { index: number; item: T }) {
    return array.map((item, index) => {
      if (index !== action.index) {
        // This isn't the item we care about - keep it as-is
        return item
      }
      // Otherwise, this is the one we want - return an updated value
      return {
        ...item,
        ...action.item,
      }
    })
  }
}
